// ============================================================
// DAMAGE APPLICATION
// ============================================================
//
// Session 6b: simplified to direct totalSoldiers subtraction (the
// 0.2/0.8 dead/wounded split + `wounded` field are gone). One unit
// of damage = one fewer soldier; when totalSoldiers hits 0 the unit
// dies. See SRS §3.3.4 / §5.4 for rationale.
//
// Mutates the passed unit. Callers that need referential transparency
// must clone the unit (see `clone.ts`) before invocation.

import type { BattleUnit, Base } from '../BattleUnit.ts';

export interface DamageDelta {
  /** Total damage applied (clamped — may be less than requested if unit was dying). */
  dmg: number;
  died: boolean;
  totalSoldiers: number;
}

/** SRS §5.4 — apply `dmg` to unit, returns post-hit snapshot.
 *
 *  Session 11 follow-up — `BattleUnit.dmgTakenMult` (vulnerability
 *  multiplier driven by `'defDown'` modifiers) is applied centrally
 *  HERE so every damage path benefits uniformly:
 *    - Step C combat (physical, after tickDefPhys)
 *    - Step D hostile spells (damage / rainPuddle / Lightning, after tickDefMag)
 *    - Step F terrain DOT (burningGrass, after tickDefMag)
 *
 *  Callers still apply tickDefPhys / tickDefMag externally (the reduce-
 *  THEN-amplify order is mathematically commutative anyway). Default
 *  mult = 1.0 → identity, fully back-compatible with pre-defDown fixtures.
 */
export function applyDamageToUnit(u: BattleUnit, dmg: number): DamageDelta {
  const before = u.totalSoldiers;
  // dmg arg is the post-defense reduced amount the caller computed.
  // Vulnerability scales it up at this final subtraction point.
  const mult = typeof u.dmgTakenMult === 'number' && u.dmgTakenMult > 0
    ? u.dmgTakenMult
    : 1;
  const finalDmg = Math.ceil(dmg * mult);
  u.totalSoldiers = Math.max(0, before - finalDmg);
  if (u.totalSoldiers <= 0) {
    u.dead = true;
    u.totalSoldiers = 0;
  }
  return {
    dmg: before - u.totalSoldiers,
    died: u.dead,
    totalSoldiers: u.totalSoldiers,
  };
}

export interface BaseDamageDelta {
  dmg: number;
  destroyed: boolean;
  hp: number;
}

/** Apply `dmg` to base, mutating it. */
export function applyDamageToBase(b: Base, dmg: number): BaseDamageDelta {
  b.hp -= dmg;
  if (b.hp <= 0) { b.hp = 0; b.destroyed = true; }
  return { dmg, destroyed: b.destroyed, hp: b.hp };
}
